import api from './api';

export interface PaymentHistory {
  id: string;
  created_at: string;
  amount: number;
  payment_method: 'credit' | 'pix';
  status: 'completed' | 'pending' | 'failed';
  card_last_digits?: string;
}

export interface UsageData {
  total_tokens: number;
  total_documents: number;
  available_tokens: number;
  tokens_per_credit: number;
  chat_history: {
    id: string;
    created_at: string;
    tokens_used: number;
  }[];
  document_history: {
    id: string;
    created_at: string;
    tokens_used: number;
    document_type: string;
  }[];
  payment_history: PaymentHistory[];
}

export const getUsageData = async (): Promise<UsageData> => {
  const response = await fetch('/usage', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch usage data');
  }

  return response.json();
};

export const addCredits = async (amount: number, payment_method: string, card_data?: any): Promise<void> => {
  const response = await fetch('/users/add-credits', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`
    },
    body: JSON.stringify({
      amount,
      payment_method,
      card_data
    }),
  });

  if (!response.ok) {
    throw new Error('Failed to add credits');
  }
};

export const getUserUsage = async (): Promise<UsageData> => {
  try {
    const response = await api.get('/usage');
    
    // Log para debug
    console.log('Raw API response:', response.data);
    
    // Mapear a resposta do backend para o formato esperado pelo frontend
    const data = response.data;
    
    // Criar objeto de retorno com valores padrão para evitar undefined/null
    const result: UsageData = {
      total_tokens: 0,
      total_documents: 0,
      available_tokens: 0,
      tokens_per_credit: 20,
      chat_history: [],
      document_history: [],
      payment_history: []
    };
    
    // Preencher com os dados da API, se disponíveis
    if (data) {
      // Campos numéricos
      result.total_tokens = typeof data.total_tokens === 'number' ? data.total_tokens : 0;
      result.total_documents = typeof data.total_documents === 'number' ? data.total_documents : 0;
      result.available_tokens = typeof data.available_tokens === 'number' ? data.available_tokens : 
                               (typeof data.credits_remaining === 'number' ? data.credits_remaining : 0);
      result.tokens_per_credit = typeof data.tokens_per_credit === 'number' ? data.tokens_per_credit : 20;
      
      // Arrays
      if (Array.isArray(data.chat_history)) {
        result.chat_history = data.chat_history.map((item: any) => ({
          id: String(item.id || ''),
          created_at: String(item.created_at || new Date().toISOString()),
          tokens_used: typeof item.tokens_used === 'number' ? item.tokens_used : 0
        }));
      }
      
      if (Array.isArray(data.document_history)) {
        result.document_history = data.document_history.map((item: any) => ({
          id: String(item.id || ''),
          created_at: String(item.created_at || new Date().toISOString()),
          tokens_used: typeof item.tokens_used === 'number' ? item.tokens_used : 0,
          document_type: String(item.document_type || 'document')
        }));
      }
      
      if (Array.isArray(data.payment_history)) {
        result.payment_history = data.payment_history.map((item: any) => ({
          id: String(item.id || ''),
          created_at: String(item.created_at || new Date().toISOString()),
          amount: typeof item.amount === 'number' ? item.amount : 0,
          payment_method: item.payment_method as 'credit' | 'pix' || 'credit',
          status: item.status as 'completed' | 'pending' | 'failed' || 'completed',
          card_last_digits: item.card_last_digits
        }));
      }
    }
    
    console.log('Mapped usage data:', result);
    return result;
  } catch (error) {
    console.error('Error getting usage data:', error);
    throw error;
  }
};

export const checkPaymentStatus = async (paymentId: string) => {
  try {
    const response = await api.post(`/usage/payments/${paymentId}/check-status`);
    return response.data;
  } catch (error) {
    console.error('Error checking payment status:', error);
    throw error;
  }
};

export const getUserPayments = async () => {
  try {
    const response = await api.get('/usage/payments');
    return response.data;
  } catch (error) {
    console.error('Error fetching payments:', error);
    throw error;
  }
};

export const getPaymentStatusForPolling = async (paymentId: string) => {
  try {
    const response = await api.get(`/usage/payments/${paymentId}/status`);
    return response.data;
  } catch (error) {
    console.error('Erro ao consultar status do pagamento:', error);
    throw error;
  }
};

export const startPaymentPolling = (
  paymentId: string,
  onStatusUpdate: (status: string, source?: string, details?: any, pollInfo?: {count: number, max: number}) => void,
  onComplete: () => void,
  onError: (error: any) => void
) => {
  const POLLING_INTERVAL = 10000; // 10 segundos
  const MAX_POLLING_TIME = 600000; // 10 minutos máximo (aumentado de 5 para 10 minutos)
  
  let pollCount = 0;
  const maxPolls = MAX_POLLING_TIME / POLLING_INTERVAL;
  
  console.log(`🚀 [POLLING] INICIANDO SISTEMA DE POLLING INTELIGENTE`);
  console.log(`🚀 [POLLING] Payment ID: ${paymentId}`);
  console.log(`🚀 [POLLING] Intervalo: ${POLLING_INTERVAL}ms (${POLLING_INTERVAL/1000}s)`);
  console.log(`🚀 [POLLING] Máximo de tentativas: ${maxPolls}`);
  console.log(`🚀 [POLLING] Tempo limite total: ${MAX_POLLING_TIME/1000}s (${MAX_POLLING_TIME/60000} min)`);
  console.log(`🚀 [POLLING] Timestamp inicial: ${new Date().toISOString()}`);
  
  const pollInterval = setInterval(async () => {
    try {
      pollCount++;
      const startTime = Date.now();
      
      console.log(`\n📡 [POLLING] === TENTATIVA ${pollCount}/${maxPolls} ===`);
      console.log(`📡 [POLLING] Timestamp: ${new Date().toISOString()}`);
      console.log(`📡 [POLLING] Fazendo requisição para: /usage/payments/${paymentId}/status`);
      
      const result = await getPaymentStatusForPolling(paymentId);
      
      const endTime = Date.now();
      const responseTime = endTime - startTime;
      
      console.log(`📡 [POLLING] Resposta recebida em ${responseTime}ms`);
      console.log(`📡 [POLLING] Status retornado: ${result.status}`);
      console.log(`📡 [POLLING] Fonte: ${result.source || 'unknown'}`);
      console.log(`📡 [POLLING] External ID: ${result.external_id || 'N/A'}`);
      console.log(`📡 [POLLING] Amount: ${result.amount || 'N/A'}`);
      console.log(`📡 [POLLING] Payment Method: ${result.payment_method || 'N/A'}`);
      console.log(`📡 [POLLING] Created At: ${result.created_at || 'N/A'}`);
      console.log(`📡 [POLLING] Updated At: ${result.updated_at || 'N/A'}`);
      
      const source = result.source || 'unknown';
      const statusMsg = `🔄 Polling ${pollCount}/${maxPolls} - Status: ${result.status} (${source})`;
      console.log(`📡 [POLLING] ${statusMsg}`);
      
      // Informar ao frontend sobre a atualização com mais detalhes
      console.log(`📡 [POLLING] Chamando onStatusUpdate...`);
      onStatusUpdate(result.status, source, result, { count: pollCount, max: maxPolls });
      console.log(`📡 [POLLING] onStatusUpdate executado com sucesso`);
      
      // Se o pagamento foi concluído (aprovado ou falhou), parar o polling
      const paymentStatus = result.payment_status || result.status; // Suporte a ambos os formatos
      if (paymentStatus === 'completed' || paymentStatus === 'failed') {
        const finalMsg = source === 'webhook_processed' 
          ? `✅ Polling finalizado - Webhook processou: ${paymentStatus}`
          : `✅ Polling finalizado - Detectado via API: ${paymentStatus}`;
        console.log(`\n🎉 [POLLING] FINALIZAÇÃO DETECTADA!`);
        console.log(`🎉 [POLLING] ${finalMsg}`);
        console.log(`🎉 [POLLING] Total de tentativas: ${pollCount}/${maxPolls}`);
        console.log(`🎉 [POLLING] Tempo total decorrido: ${(pollCount * POLLING_INTERVAL)/1000}s`);
        console.log(`🎉 [POLLING] Parando o polling...`);
        clearInterval(pollInterval);
        
        console.log(`🎉 [POLLING] Chamando onComplete...`);
        onComplete();
        console.log(`🎉 [POLLING] Processo de polling finalizado com sucesso!`);
        return;
      }
      
      // Se chegou ao máximo de tentativas, parar o polling
      if (pollCount >= maxPolls) {
        console.log(`\n⏰ [POLLING] TEMPO LIMITE EXCEDIDO!`);
        console.log(`⏰ [POLLING] Tentativas realizadas: ${pollCount}/${maxPolls}`);
        console.log(`⏰ [POLLING] Tempo total: ${(pollCount * POLLING_INTERVAL)/1000}s`);
        console.log(`⏰ [POLLING] Status final: ${paymentStatus} (${source})`);
        console.log(`⏰ [POLLING] Parando o polling por timeout...`);
        clearInterval(pollInterval);
        onError(new Error('Tempo limite excedido para verificar o pagamento'));
        return;
      }
      
      console.log(`📡 [POLLING] Próxima verificação em ${POLLING_INTERVAL/1000}s...`);
      
    } catch (error: any) {
      console.log(`\n❌ [POLLING] ERRO DURANTE POLLING!`);
      console.error(`❌ [POLLING] Tentativa: ${pollCount}/${maxPolls}`);
      console.error(`❌ [POLLING] Erro:`, error);
      console.error(`❌ [POLLING] Erro message:`, error.message);
      console.error(`❌ [POLLING] Erro stack:`, error.stack);
      if (error.response) {
        console.error(`❌ [POLLING] Response status:`, error.response.status);
        console.error(`❌ [POLLING] Response data:`, error.response.data);
      }
      console.log(`❌ [POLLING] Parando o polling devido ao erro...`);
      clearInterval(pollInterval);
      onError(error);
    }
  }, POLLING_INTERVAL);
  
  // Retornar função para cancelar o polling
  return () => {
    console.log(`\n🛑 [POLLING] CANCELAMENTO MANUAL DO POLLING`);
    console.log(`🛑 [POLLING] Payment ID: ${paymentId}`);
    console.log(`🛑 [POLLING] Tentativas realizadas: ${pollCount}/${maxPolls}`);
    console.log(`🛑 [POLLING] Timestamp: ${new Date().toISOString()}`);
    clearInterval(pollInterval);
  };
}; 